import { Link } from "react-router-dom";
import { CheckCircle2, Clock } from "lucide-react";
import { PriceBreakdown } from "./PriceBreakdown";
import { fmt12 } from "./AppointmentPicker";
import { money } from "@/lib/api";

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between gap-4 border-b border-slate-100 py-2.5 text-sm">
    <span className="text-slate-500">{label}</span>
    <span data-testid={testid} className="text-right font-medium text-[#0B132B]">{value}</span>
  </div>
);

export const StepReview = ({ f, svc, quote }) => (
  <div className="grid gap-8 lg:grid-cols-2">
    <div>
      <Row label="Selected service" value={svc?.name} testid="review-service" />
      <Row label="Property address" value={`${f.address}, ${f.city}, ${f.state} ${f.zip}`} testid="review-address" />
      <Row label="Appointment" value={f.date ? `${f.date} at ${fmt12(f.time)}` : "-"} testid="review-appointment" />
      <Row label="Base inspection price" value={money(quote?.base_price)} testid="review-base" />
      <Row label="Size adjustment" value={quote?.custom_quote ? "Custom quote" : money(quote?.size_adjustment)} testid="review-size" />
      <Row label="Additional services" value={money(quote?.additional_total)} testid="review-additional" />
      <Row label="Travel fee" value={money(quote?.travel_fee)} testid="review-travel" />
    </div>
    <PriceBreakdown quote={quote} testid="review-breakdown" />
  </div>
);

export const StepSubmit = ({ f, set, svc, quote, submitting, onSubmit }) => (
  <div className="mx-auto max-w-xl text-center">
    <p className="text-sm text-slate-500">You're requesting</p>
    <p className="mt-1 font-display text-2xl font-bold text-[#0B132B]">{svc?.name}</p>
    <p className="mt-1 text-sm text-slate-600">{f.date} at {fmt12(f.time)} · {f.address}, {f.city}</p>
    <p data-testid="submit-total" className="mt-4 font-display text-4xl font-bold text-[#0B132B]">{money(quote?.total)}</p>
    <p className="text-xs text-slate-400">Final estimated total</p>
    <input type="text" name="website" value={f.website} onChange={set} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
    <button type="button" onClick={onSubmit} disabled={submitting} data-testid="confirm-inspection-request-btn"
      className="mt-8 w-full rounded-full bg-gold py-4 text-base font-bold text-[#0B132B] transition-colors hover:bg-[#E5C158] disabled:opacity-60">
      {submitting ? "Sending request…" : "Confirm Inspection Request"}
    </button>
    <p className="mt-4 text-xs text-slate-500">Your appointment is a request until our team approves it. You'll receive an email once it's confirmed.</p>
  </div>
);

export const BookingDone = ({ booking }) => (
  <div data-testid="booking-confirmation" className="mx-auto max-w-xl rounded-3xl border border-slate-200 bg-white p-10 text-center">
    <CheckCircle2 className="mx-auto h-14 w-14 text-gold" />
    <h2 className="mt-5 font-display text-3xl font-bold text-[#0B132B]">Request received</h2>
    <p className="mt-3 text-slate-600">Your booking reference number is</p>
    <p data-testid="booking-reference" className="mt-2 font-mono-x text-2xl font-bold tracking-widest text-[#0B132B]">{booking.reference}</p>
    <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700" data-testid="booking-status">
      <Clock className="h-4 w-4" /> Pending approval
    </div>
    <p className="mt-6 text-sm leading-relaxed text-slate-500">
      Requested time: {booking.date} at {fmt12(booking.time)}. This appointment is not confirmed yet. We'll review your request
      and email {booking.email} once it is approved.
    </p>
    <Link to="/property-inspections" className="mt-8 inline-block text-sm font-semibold text-gold underline underline-offset-4">Back to inspections</Link>
  </div>
);
